import { Component } from '@theme/component';
import { scheduler } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Carousel Progress this is a custom element that allows for the ability to have a progress bar and arrows for a carousel.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} progressFill
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class CarouselProgress extends Component {
  requiredRefs = ['progressFill'];

  #controller = new AbortController();
  /** @type {number | null} */
  #dragUpdateId = null;

  connectedCallback() {
    super.connectedCallback();

    /** @type {import('./slideshow').Slideshow | null} */
    this.slideshow =
      this.closest('.resource-list__carousel')?.querySelector('slideshow-component') ||
      this.closest('.media-gallery--carousel')?.querySelector('slideshow-component') ||
      this.closest('media-gallery')?.querySelector('slideshow-component');

    if (!this.slideshow) {
      console.warn('CarouselProgress: No slideshow component found');
      return;
    }

    const { signal } = this.#controller;

    this.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    if (this.refs.previous) {
      this.refs.previous.addEventListener('click', this.#handlePrevious, { signal });
    }

    if (this.refs.next) {
      this.refs.next.addEventListener('click', this.#handleNext, { signal });
    }

    setTimeout(() => {
      if (!this.slideshow) return;

      const currentIndex = this.slideshow.current || 0;
      const initialIndex = this.slideshow.initialSlideIndex || 0;

      if (initialIndex !== 0 && currentIndex === 0) {
        this.slideshow.select(initialIndex, undefined, { animate: false });
      }

      this.#updateProgress();
    }, 100);

    this.#updateProgress();

    window.addEventListener('resize', this.#updateProgress, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#stopDragUpdates();
  }

  /**
   * Handles slideshow select events
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const { trigger, index } = event.detail;

    if (trigger === 'drag') {
      this.#startDragUpdates();
    } else {
      this.#stopDragUpdates();
    }

    this.#updateProgress();
    this.#updateNavigationState(index);
  };

  /**
   * Starts real-time updates during drag
   */
  #startDragUpdates = () => {
    if (this.#dragUpdateId) return;

    this.#dragUpdateId = setInterval(() => {
      this.#updateProgressDuringDrag();
    }, 16);
  };

  /**
   * Stops real-time updates
   */
  #stopDragUpdates = () => {
    if (this.#dragUpdateId) {
      clearInterval(this.#dragUpdateId);
      this.#dragUpdateId = null;
    }
  };

  /**
   * Function to update the progress bar and the button
   */
  #updateProgress = () => {
    if (!this.slideshow || !this.refs.progressFill) return;

    const currentIndex = this.slideshow.current || 0;
    const totalSlides = this.slideshow.slides?.length || 1;

    const minProgress = 15;
    const maxProgress = 100;
    const progress =
      totalSlides > 1 ? minProgress + (currentIndex / (totalSlides - 1)) * (maxProgress - minProgress) : minProgress;

    scheduler.schedule(() => {
      this.refs.progressFill.style.width = `${progress}%`;
    });

    if (this.refs.previous) {
      this.refs.previous.disabled = currentIndex === 0;
    }
    if (this.refs.next) {
      this.refs.next.disabled = currentIndex === totalSlides - 1;
    }
  };

  /**
   * Updates progress bar during drag
   */
  #updateProgressDuringDrag = () => {
    if (!this.slideshow || !this.refs.progressFill) return;

    const totalSlides = this.slideshow.slides?.length || 1;
    if (totalSlides <= 1) return;

    const scroller = this.slideshow.querySelector('slideshow-slides');
    if (!scroller) {
      this.#updateProgress();
      return;
    }

    // the minprogress is setting the progress bar to 15% when the carousel is at the start can change this to 0 if want to keep it not prefilled.
    const minProgress = 15;
    const maxProgress = 100;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    if (maxScroll <= 0) {
      this.#updateProgress();
      return;
    }

    const scrollProgress = Math.max(0, Math.min(1, scroller.scrollLeft / maxScroll));
    const progress = minProgress + scrollProgress * (maxProgress - minProgress);

    scheduler.schedule(() => {
      this.refs.progressFill.style.width = `${progress}%`;
    });
  };

  /**
   * Handles previous button click
   * @param {Event} event
   */
  #handlePrevious = (event) => {
    event.preventDefault();
    if (this.slideshow) this.slideshow.previous();
  };

  /**
   * Handles next button click
   * @param {Event} event
   */
  #handleNext = (event) => {
    event.preventDefault();
    if (this.slideshow) this.slideshow.next();
  };

  /**
   * Selects a specific slide by index
   * @param {number} index - The slide index to select
   * @param {Event} event
   */
  selectSlide = (index, event) => {
    event?.preventDefault();
    if (this.slideshow) {
      this.slideshow.select(index, event);
      this.#updateNavigationState(index);
    }
  };

  /**
   * Updates the navigation state for collection navigation
   * @param {number} activeIndex - The currently active slide index
   */
  #updateNavigationState = (activeIndex) => {
    const navigationContainer = this.closest('.section-resource-list')?.querySelector('.collection-navigation');
    if (!navigationContainer) return;

    const navItems = navigationContainer.querySelectorAll('.collection-nav-item');
    navItems.forEach((item, index) => {
      item.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
    });
  };
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
